import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Spinner } from "../../components";
import { isValidEmail } from "../../utils/validators";
import { AuthenticationService } from "../../authentication/AuthenticationService";
import { detectPartnerFromHost } from "../../partner/detect";

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [emailError, setEmailError] = useState("");

  const partnerPreset = detectPartnerFromHost();
  const loginImage = partnerPreset.loginImgHref;

  const authService = new AuthenticationService();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    
    // Clear error when user starts typing
    if (emailError) {
      setEmailError("");
    }
  };

  const handleEmailBlur = () => {
    // Validate email when user leaves the input field
    if (email.trim() && !isValidEmail(email)) {
      setEmailError("Please enter a valid email address");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email before submission
    if (!email.trim()) {
      setEmailError("Email address is required");
      return;
    }
    
    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    
    setIsSubmitting(true);

    try {
      await authService.requestPasswordReset(email);
      setIsSubmitting(false);
      setIsEmailSent(true);
    } catch (error) {
      console.error("Failed to send password reset email:", error);
    } finally {
      setIsSubmitting(false);
    }

    
    
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit(e as any);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };


  return (
    <div className="min-h-screen flex">
      <div
        className="w-2/5 relative flex items-center justify-center p-8 bg-cover bg-center"
        style={{ backgroundImage: `url(${loginImage})` }}
      >
      </div>

      {/* Right Side - 60% */}
      <div className="w-3/5 flex items-center justify-center bg-gray-50 p-8">
        <div className="w-full max-w-md space-y-8">
          {!isEmailSent ? (
            <>
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Forgot Password?
                </h2>
                <p className="text-gray-600">
                  Enter your email address and we'll send you a link to reset
                  your password
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4 items-center"
              >
                <div className="w-full">
                  <Input
                    type="email"
                    label="Email Address"
                    placeholder="Enter your email"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    onKeyDown={handleKeyDown}
                    required
                    aria-label="Email address input"
                    tabIndex={0}
                    error={emailError}
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full mt-4"
                  tabIndex={0}
                  aria-label="Reset password"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleSubmit(e as any);
                    }
                  }}
                  disabled={isSubmitting || !email.trim() || !isValidEmail(email)}
                >
                  {isSubmitting ? "Sending" : "Reset password"}
                  {isSubmitting && <Spinner className="ml-2" />}
                </Button>

                <div className="text-sm">
                  <button
                    type="button"
                    className="font-medium text-black hover:text-slate-700 focus:outline-none focus:underline"
                    tabIndex={0}
                    aria-label="Back to login link"
                    onClick={handleBackToLogin}
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Check Your Inbox
                </h2>
                <p className="text-gray-600 mb-4">
                  We've sent a password reset link to <strong>{email}</strong>
                </p>
              </div>

              <div className="flex flex-col gap-4 items-center">
                <p className="text-sm text-gray-500">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={() => {
                    setIsEmailSent(false);
                    setEmail("");
                    setEmailError("");
                    
                  }}
                >
                  Send email again
                </Button>

                <div className="text-sm">
                  <button
                    type="button"
                    className="font-medium text-black hover:text-slate-700 focus:outline-none focus:underline"
                    tabIndex={0}
                    aria-label="Back to login link"
                    onClick={handleBackToLogin}
                  >
                    Back to Login
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
